/**
 * Message Store
 * Manages the last seen timestamp for chat messages per game.
 * This ensures consistent "new message" detection across multiple consumers.
 */

import { create } from 'zustand'

interface MessageState {
  // Map of gameId to last seen message timestamp
  lastSeenTimestamp: Record<string, number>
}

interface MessageActions {
  // Update the last seen timestamp for a game
  updateLastSeen: (gameId: string, timestamp: number) => void

  // Get the last seen timestamp for a game
  getLastSeen: (gameId: string) => number

  // Clear timestamp for a game (useful when leaving a game)
  clearGame: (gameId: string) => void

  // Clear all timestamps (useful on logout)
  clearAll: () => void
}

const initialState: MessageState = {
  lastSeenTimestamp: {}
}

export const useMessageStore = create<MessageState & MessageActions>((set, get) => ({
  ...initialState,

  updateLastSeen: (gameId: string, timestamp: number) =>
    set((state) => {
      const current = state.lastSeenTimestamp[gameId] || 0
      // Only update if newer (prevents unnecessary re-renders)
      if (timestamp <= current) return state
      return {
        lastSeenTimestamp: {
          ...state.lastSeenTimestamp,
          [gameId]: timestamp
        }
      }
    }),

  getLastSeen: (gameId: string) =>
    get().lastSeenTimestamp[gameId] || 0,

  clearGame: (gameId: string) =>
    set((state) => {
      const { [gameId]: _, ...rest } = state.lastSeenTimestamp
      return { lastSeenTimestamp: rest }
    }),

  clearAll: () => set(initialState)
}))
